import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Maximize, Minimize } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Sponsor full-screen component ────────────────────────────────────────────
function SponsorScreen({ sponsor, onToggle, isFullscreen }) {
  const hasLogo = !!sponsor?.logoUrl;
  const hasText = !!sponsor?.text;

  return (
    <div className="h-screen w-screen bg-white relative overflow-hidden">
      {/* Fullscreen toggle */}
      <button
        onClick={onToggle}
        className="absolute top-3 right-3 z-20 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded p-1.5 transition-colors"
      >
        {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
      </button>

      {/* Logo fills the entire screen */}
      {hasLogo && (
        <img
          src={`${BACKEND_URL}${sponsor.logoUrl}`}
          alt="Sponsor"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: hasText ? '80%' : '100%',
            objectFit: 'contain',
          }}
        />
      )}

      {/* Text — anchored to bottom when logo present, else centred */}
      {hasText && (
        <div
          className="absolute left-0 right-0 flex items-center justify-center font-bold text-gray-900 text-center leading-tight"
          style={{
            bottom: 0,
            top: hasLogo ? '80%' : 0,
            padding: '1vh 4vw',
            fontSize: hasLogo ? 'clamp(32px, 4vw, 80px)' : 'clamp(48px, 8vw, 140px)',
          }}
        >
          {sponsor.text}
        </div>
      )}

      {!hasLogo && !hasText && (
        <div className="w-full h-full flex items-center justify-center">
          <p className="text-gray-300 text-2xl">No sponsor content</p>
        </div>
      )}
    </div>
  );
}

// ── SMS Register full-screen component ──────────────────────────────────────
function SmsRegisterScreen({ onToggle, isFullscreen }) {
  return (
    <div className="h-screen w-screen bg-white flex flex-col items-center justify-center gap-6 px-12 py-10 relative overflow-hidden">
      <button
        onClick={onToggle}
        className="absolute top-3 right-3 z-20 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded p-1.5 transition-colors"
      >
        {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
      </button>
      <img src="/scorz-logo.png" alt="Scorz" className="h-20 mx-auto" onError={(e) => e.target.style.display = 'none'} />
      <p
        className="text-gray-800 text-center font-semibold leading-snug max-w-3xl"
        style={{ fontSize: 'clamp(20px, 2.5vw, 48px)' }}
      >
        Scan the on-screen QR Code or visit the link below to register for instant SMS messages when your gymnast receives a score.
      </p>
      <QRCodeSVG value="https://scorz.co.uk/register" size={200} level="M" />
      <p
        className="text-gray-600 font-bold tracking-wide"
        style={{ fontSize: 'clamp(18px, 2vw, 40px)' }}
      >
        scorz.co.uk/register
      </p>
    </div>
  );
}

// WAG apparatus for the 4-panel grid — always in this order
const PANEL_CELLS = [
  { label: 'VAULT',        keys: ['vault', 'wagvault', 'magvault'] },
  { label: 'BARS',         keys: ['bars', 'wagbars'] },
  { label: 'BEAM',         keys: ['beam', 'wagbeam'] },
  { label: 'FLOOR',        keys: ['floor', 'wagfloor', 'magfloor'] },
];

// Find the latest score for a cell from latestScores, for the given panel letter
function cellScore(latestScores, cell, panelLetter) {
  for (const key of cell.keys) {
    const panelData = latestScores[key];
    if (panelData) {
      const score = panelData[panelLetter] || panelData[panelLetter.toLowerCase()];
      if (score) return score;
    }
  }
  return null;
}

// Auto-size name font based on length
function nameFontSize(name) {
  const longest = name.split(' ').reduce((a, b) => a.length > b.length ? a : b, '');
  if (longest.length <= 5)  return '80px';
  if (longest.length <= 7)  return '65px';
  if (longest.length <= 9)  return '54px';
  if (longest.length <= 12) return '44px';
  return '34px';
}

export default function DisplayPanelPage({ panel }) {
  const [showSetup, setShowSetup] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(
    () => localStorage.getItem(`display_panel_${panel}_competition_id`) || ''
  );
  const [selectedRound, setSelectedRound] = useState(null);
  const [availableRounds, setAvailableRounds] = useState([1, 2, 3]);

  const [latestScores, setLatestScores] = useState({});
  const [nowPlaying, setNowPlaying] = useState(null);
  const [nextUp, setNextUp] = useState(null);
  const [timer, setTimer] = useState(null);
  const [displayTime, setDisplayTime] = useState(0);
  const [displayMode, setDisplayMode] = useState('info'); // 'info' | 'scores'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [presentationState, setPresentationState] = useState(null);
  const [showAllResults, setShowAllResults] = useState(false);

  // Display control (sponsor modes)
  const [displayControlMode, setDisplayControlMode] = useState('normal');
  const [sponsors, setSponsors] = useState([]);
  const [, setClockTick] = useState(0);  // wall-clock sync placeholder (see below)
  const prevTimerRunningRef = useRef(false);

  const competitionIdRef = useRef(selectedCompetitionId);
  const selectedRoundRef  = useRef(selectedRound);
  const isFetchingRef     = useRef(false);
  const forceInfoUntilRef = useRef(null);
  const prevNowPlayingRef = useRef(null);
  const prevNextUpRef     = useRef(null);

  useEffect(() => { competitionIdRef.current = selectedCompetitionId; }, [selectedCompetitionId]);
  useEffect(() => { selectedRoundRef.current = selectedRound; }, [selectedRound]);

  // Load competitions
  useEffect(() => {
    axios.get(`${API}/music/competitions`).then(res => {
      const comps = res.data?.competitions || [];
      setCompetitions(comps);
      const saved = localStorage.getItem(`display_panel_${panel}_competition_id`);
      if (saved && comps.find(c => c.id === saved)) setShowSetup(false);
    }).catch(() => {});
  }, [panel]);

  // Fetch rounds when competition changes
  useEffect(() => {
    if (!selectedCompetitionId) return;
    axios.get(`${API}/music/programme/${selectedCompetitionId}`)
      .then(res => {
        const rounds = [...new Set((res.data?.programme || []).map(p => p.round).filter(Boolean))].sort((a, b) => a - b);
        setAvailableRounds(rounds.length ? rounds : [1, 2, 3]);
      }).catch(() => setAvailableRounds([1, 2, 3]));
  }, [selectedCompetitionId]);

  // Combined display state poll
  const fetchDisplayState = useCallback(async () => {
    const compId = competitionIdRef.current;
    if (!compId || isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const [timerRes, nowRes, nextRes, presRes] = await Promise.all([
        axios.get(`${API}/timer/state/${compId}`).catch(() => null),
        axios.get(`${API}/now-playing/${compId}`).catch(() => null),
        axios.get(`${API}/music-queue/${compId}/next-up`).catch(() => null),
        axios.get(`${API}/presentation/state/${compId}`).catch(() => null),
      ]);
      const td = timerRes?.data?.timer;
      if (td?.isRunning) {
        setTimer(td);
        if (!td.isPaused) {
          const elapsed = Math.floor((Date.now() - new Date(td.startedAt).getTime()) / 1000);
          setDisplayTime(Math.max(0, td.duration - elapsed));
        } else {
          setDisplayTime(td.remainingTime || 0);
        }
      } else { setTimer(null); setDisplayTime(0); }
      setNowPlaying(nowRes?.data?.nowPlaying || null);
      setNextUp(nextRes?.data?.nextUp || null);
      // Presentation — only update if something changed (avoid re-render churn)
      const newPres = presRes?.data || null;
      setPresentationState(prev => {
        const key = s => s ? `${s.isActive}|${s.category}|${s.apparatus}|${(s.revealedPlaces||[]).join(',')}` : 'null';
        return key(prev) === key(newPres) ? prev : newPres;
      });
      if (newPres?.isActive && (newPres?.revealedPlaces?.length ?? 0) > 0) {
        const maxRevealed = Math.max(...(newPres.revealedPlaces || [0]));
        setShowAllResults(maxRevealed <= 1);
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  const fetchLatestScores = useCallback(async () => {
    const compId = competitionIdRef.current;
    const round  = selectedRoundRef.current;
    if (!compId) return;
    try {
      const roundParam = round ? `?round=${round}` : '';
      const res = await axios.get(`${API}/display/latest-scores/${compId}${roundParam}`);
      setLatestScores(res.data.scores || {});
    } catch (_) {}
  }, []);

  // Single poll
  useEffect(() => {
    if (!selectedCompetitionId) return;
    fetchDisplayState();
    fetchLatestScores();
    const s = setInterval(fetchDisplayState, 1000);
    const c = setInterval(fetchLatestScores, 5000);
    return () => { clearInterval(s); clearInterval(c); };
  }, [selectedCompetitionId, fetchDisplayState, fetchLatestScores]);

  // Timer countdown
  useEffect(() => {
    if (!timer?.isRunning || timer.isPaused) return;
    const id = setInterval(() => setDisplayTime(prev => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(id);
  }, [timer]);

  // Rotation logic — force info for 20s on gymnast change, then rotate every 20s
  useEffect(() => {
    const npId = nowPlaying?.id || nowPlaying?.gymnastId;
    const nuId = nextUp?.number ? `${nextUp.number}${nextUp.letter}` : null;
    if (npId && npId !== prevNowPlayingRef.current) {
      setDisplayMode('info');
      forceInfoUntilRef.current = Date.now() + 20000;
    } else if (!nowPlaying && nuId && nuId !== prevNextUpRef.current) {
      setDisplayMode('info');
      forceInfoUntilRef.current = Date.now() + 20000;
    }
    prevNowPlayingRef.current = npId;
    prevNextUpRef.current = nuId;
  }, [nowPlaying, nextUp]);

  useEffect(() => {
    if (timer?.isRunning) return;
    const id = setInterval(() => {
      if (forceInfoUntilRef.current && Date.now() < forceInfoUntilRef.current) return;
      setDisplayMode(prev => prev === 'info' ? 'scores' : 'info');
    }, 20000);
    return () => clearInterval(id);
  }, [timer]);

  // ── Poll display control mode + sponsors (every 2 s) ─────────────────────────
  useEffect(() => {
    if (!selectedCompetitionId) return;
    const fetchControl = async () => {
      try {
        const res = await axios.get(`${API}/display-control/${selectedCompetitionId}`);
        setDisplayControlMode(res.data?.mode || 'normal');
        setSponsors(res.data?.sponsors || []);
      } catch (_) {}
    };
    fetchControl();
    const id = setInterval(fetchControl, 2000);
    return () => clearInterval(id);
  }, [selectedCompetitionId]);

  // ── 10-second clock tick for wall-clock-synced sponsor slot ─────────────────
  // Fires every 1s so the slot updates within 1s of the 10s boundary.
  useEffect(() => {
    const id = setInterval(() => setClockTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Reset slot when mode changes ─────────────────────────────────────────────
  // (no-op with clock-based slot — kept for clarity)

  // ── Reset slot to timer-first when timer starts in timer_sponsors mode ────────
  // (no-op with clock-based slot — all displays use wall-clock epoch alignment)

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleStart = () => {
    localStorage.setItem(`display_panel_${panel}_competition_id`, selectedCompetitionId);
    setShowSetup(false);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ─── Setup screen ────────────────────────────────────────────────────────────
  if (showSetup) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6 border rounded-xl p-6 shadow-sm">
          <div className="text-center">
            <img src="/scorz-logo.png" alt="Scorz" className="h-12 mx-auto mb-3" />
            <h2 className="text-xl font-bold">Display — Panel {panel}</h2>
            <p className="text-sm text-gray-500 mt-1">4-panel score view for Panel {panel}</p>
          </div>
          <div className="space-y-3">
            <Select value={selectedCompetitionId} onValueChange={setSelectedCompetitionId}>
              <SelectTrigger><SelectValue placeholder="Select competition" /></SelectTrigger>
              <SelectContent>
                {competitions.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedRound || 'all'} onValueChange={v => setSelectedRound(v === 'all' ? null : v)}>
              <SelectTrigger><SelectValue placeholder="All rounds" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Rounds</SelectItem>
                {availableRounds.map(r => <SelectItem key={r} value={String(r)}>Round {r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={handleStart} disabled={!selectedCompetitionId} className="w-full bg-black hover:bg-gray-800">
              Start Display
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Fullscreen toggle button ─────────────────────────────────────────────────
  const FullscreenBtn = () => (
    <button
      onClick={toggleFullscreen}
      className="absolute top-3 right-3 z-20 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded p-1.5 transition-colors"
    >
      {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
    </button>
  );

  // ─── Display control derived values ─────────────────────────────────────────
  // Wall-clock aligned slot — same value on all displays at any given second.
  const sponsorSlot = Math.floor(Date.now() / 10000);
  const effectiveSponsors = sponsors.filter(s => s.logoUrl || s.text);
  const hasSponsors = effectiveSponsors.length > 0;
  const currentSponsorIdx = displayControlMode === 'sponsors'
    ? (effectiveSponsors.length ? sponsorSlot % effectiveSponsors.length : 0)
    : (effectiveSponsors.length ? Math.floor(sponsorSlot / 2) % effectiveSponsors.length : 0);
  const currentSponsor = effectiveSponsors[currentSponsorIdx] || null;

  // ─── Presentation — highest priority, shows on all displays ─────────────────
  if (presentationState?.isActive) {
    const rankings = presentationState.rankings || [];
    const revealedPlaces = presentationState.revealedPlaces || [];
    const apparatus = presentationState.apparatus;
    const category = presentationState.category;

    const apparatusDisplayNames = {
      vault: 'VAULT', bars: 'BARS', beam: 'BEAM', floor: 'FLOOR',
      range: 'RANGE & CONDITIONING', randc: 'RANGE & CONDITIONING',
      aa: 'ALL AROUND', team: 'TEAM',
    };

    const groupedByRank = {};
    rankings.forEach(r => {
      if (r.rank != null) {
        if (!groupedByRank[r.rank]) groupedByRank[r.rank] = [];
        groupedByRank[r.rank].push(r);
      }
    });

    const sortedRevealed = [...revealedPlaces].sort((a, b) => a - b);
    const currentPlace = sortedRevealed.length > 0 ? sortedRevealed[0] : null;
    const showingFirst  = currentPlace === 1;
    const showingSecond = currentPlace === 2;
    const showingThird  = currentPlace === 3;

    const getRankSuffix = r => r === 1 ? 'ST' : r === 2 ? 'ND' : r === 3 ? 'RD' : 'TH';

    const getBackground = () => {
      if (showAllResults) return 'bg-white';
      if (showingFirst)  return 'bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500';
      if (showingSecond) return 'bg-gradient-to-b from-gray-200 via-gray-300 to-gray-400';
      if (showingThird)  return 'bg-gradient-to-b from-amber-500 via-amber-600 to-amber-700';
      return 'bg-white';
    };

    const getTextColor = () => {
      if (showAllResults || showingFirst || showingSecond) return 'text-black';
      if (showingThird) return 'text-white';
      return 'text-black';
    };

    const displayGymnasts = showAllResults
      ? sortedRevealed.flatMap(rank => (groupedByRank[rank] || []).map(g => ({ ...g, rank })))
      : currentPlace != null
        ? (groupedByRank[currentPlace] || []).map(g => ({ ...g, rank: currentPlace }))
        : [];
    const jointCount = displayGymnasts.length;

    return (
      <div className={`h-screen w-screen flex flex-col overflow-hidden transition-colors duration-500 ${getBackground()}`}>
        <FullscreenBtn />
        <div className="flex-1 flex flex-col items-center justify-center overflow-hidden">
          {revealedPlaces.length === 0 ? (
            <p className="text-4xl text-gray-300 tracking-widest">AWAITING RESULTS</p>
          ) : showAllResults ? (
            <div className="w-full h-full flex flex-col justify-center px-8 py-4">
              <div className="text-center mb-4">
                <p className="text-2xl text-gray-400 uppercase tracking-[0.3em]">
                  FINAL RESULTS — {(apparatusDisplayNames[apparatus] || apparatus || '').toUpperCase()}
                </p>
              </div>
              <div className="flex-1 flex flex-col justify-center space-y-3 max-h-[900px]">
                {displayGymnasts.map((gymnast) => {
                  const isFirst  = gymnast.rank === 1;
                  const isSecond = gymnast.rank === 2;
                  const isThird  = gymnast.rank === 3;
                  const isJoint  = (groupedByRank[gymnast.rank]?.length > 1);
                  let bgClass = 'bg-gray-100 text-black';
                  if (isFirst)  bgClass = 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black';
                  if (isSecond) bgClass = 'bg-gradient-to-r from-gray-300 to-gray-400 text-black';
                  if (isThird)  bgClass = 'bg-gradient-to-r from-amber-500 to-amber-600 text-white';
                  const rowHeight = Math.min(140, Math.floor(850 / displayGymnasts.length));
                  const fontSize  = Math.min(48, Math.floor(rowHeight * 0.4));
                  const rankSize  = Math.min(60, Math.floor(rowHeight * 0.5));
                  return (
                    <div key={`${gymnast.rank}-${gymnast.name}`} className={`rounded-2xl flex items-center px-8 ${bgClass}`} style={{ height: `${rowHeight}px` }}>
                      <div className={`rounded-full flex items-center justify-center font-black flex-shrink-0 ${isFirst ? 'bg-yellow-600 text-white' : isSecond ? 'bg-gray-600 text-white' : isThird ? 'bg-amber-800 text-white' : 'bg-black text-white'}`} style={{ width: `${rankSize + 20}px`, height: `${rankSize + 20}px` }}>
                        <span style={{ fontSize: `${rankSize * 0.6}px` }}>{gymnast.rank}</span>
                        <span style={{ fontSize: `${rankSize * 0.25}px` }}>{getRankSuffix(gymnast.rank)}</span>
                      </div>
                      <div className="flex-1 min-w-0 ml-6">
                        <div className="font-black truncate leading-tight" style={{ fontSize: `${fontSize}px` }}>{isJoint && '= '}{(gymnast.name || 'UNKNOWN').toUpperCase()}</div>
                        <div className="opacity-60 truncate" style={{ fontSize: `${fontSize * 0.45}px` }}>{gymnast.club || ''}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center px-4">
              {jointCount === 1 ? (
                <>
                  <div className={`font-black leading-none ${getTextColor()}`} style={{ fontSize: '280px' }}>
                    {currentPlace}<sup style={{ fontSize: '100px' }}>{getRankSuffix(currentPlace)}</sup>
                  </div>
                  <div className={`font-black text-center leading-tight mt-4 ${getTextColor()}`} style={{ fontSize: '140px' }}>
                    {(displayGymnasts[0]?.name || 'UNKNOWN').toUpperCase()}
                  </div>
                  <div className={`text-center mt-2 ${showingThird ? 'text-amber-200' : 'text-gray-500'}`} style={{ fontSize: '60px' }}>
                    {displayGymnasts[0]?.club || ''}
                  </div>
                </>
              ) : jointCount > 1 ? (
                <>
                  <div className={`font-black tracking-widest mb-4 ${getTextColor()}`} style={{ fontSize: '60px' }}>
                    = JOINT {currentPlace}{getRankSuffix(currentPlace)} PLACE =
                  </div>
                  {displayGymnasts.map((g, i) => (
                    <div key={i} className={`font-black text-center leading-tight ${getTextColor()}`} style={{ fontSize: '80px' }}>
                      {(g.name || 'UNKNOWN').toUpperCase()}
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-4xl text-gray-300 tracking-widest">AWAITING RESULTS</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Timer display (full-screen) ──────────────────────────────────────────────
  if (timer?.isRunning) {
    // Timer+Sponsors: odd slot → show sponsor
    if (displayControlMode === 'timer_sponsors' && sponsorSlot % 2 === 1 && hasSponsors) {
      return <SponsorScreen sponsor={currentSponsor} onToggle={toggleFullscreen} isFullscreen={isFullscreen} />;
    }
    return (
      <div className="h-screen bg-white flex flex-col relative overflow-hidden">
        <FullscreenBtn />
        {/* Header — matches info/scores layout */}
        <div className="flex items-center justify-center border-b border-gray-200 py-3 shrink-0">
          <img src="/scorz-logo.png" alt="Scorz" className="h-10" />
        </div>
        {/* Timer */}
        <div className="flex-1 flex items-center justify-center">
          <span
            className={`font-black tabular-nums leading-none ${displayTime <= 10 ? 'text-red-600' : 'text-gray-900'}`}
            style={{ fontSize: '28vw' }}
          >
            {formatTime(displayTime)}
          </span>
        </div>
      </div>
    );
  }

  // ─── Sponsor-only screens ────────────────────────────────────────────────────
  if (displayControlMode === 'sms_register') {
    return <SmsRegisterScreen onToggle={toggleFullscreen} isFullscreen={isFullscreen} />;
  }

  if ((displayControlMode === 'sponsors' || displayControlMode === 'timer_sponsors') && hasSponsors) {
    return <SponsorScreen sponsor={currentSponsor} onToggle={toggleFullscreen} isFullscreen={isFullscreen} />;
  }

  // ─── Info mode (now performing / next up) ─────────────────────────────────────
  if (displayMode === 'info') {
    // Only show "Now Performing" if the gymnast is on this panel (or single-panel, no letter)
    const performer = nowPlaying && (
      !nowPlaying.gymnastLetter ||
      nowPlaying.gymnastLetter.toUpperCase() === panel.toUpperCase()
    ) ? nowPlaying : null;
    const next = nextUp;   // Next Up always shows on both panels

    const nName = next ? `${next.firstName || ''} ${next.lastName || ''}`.trim() : '';

    return (
      <div className="h-screen bg-white flex flex-col relative overflow-hidden">
        <FullscreenBtn />

        {performer ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 gap-2">
            <span className="text-xs font-bold tracking-[0.3em] text-gray-400 uppercase mb-2">Now Performing</span>
            {/* Big competition number */}
            <div
              className="font-black text-black leading-none text-center"
              style={{ fontSize: 'clamp(80px, 22vw, 240px)', lineHeight: 1 }}
            >
              {performer.gymnastNumber}{performer.gymnastLetter || ''}
            </div>
            {/* Full name — uppercase, massive */}
            <div
              className="font-black text-black text-center leading-tight mt-2"
              style={{ fontSize: 'clamp(32px, 7vw, 90px)', letterSpacing: '0.04em' }}
            >
              {(performer.gymnastName || '').toUpperCase()}
            </div>
            {/* Club */}
            {performer.clubName && (
              <div
                className="text-gray-500 text-center font-semibold mt-2"
                style={{ fontSize: 'clamp(18px, 3.5vw, 50px)' }}
              >
                {performer.clubName}
              </div>
            )}
          </div>
        ) : next ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 gap-2">
            <span className="text-xs font-bold tracking-[0.3em] text-gray-400 uppercase mb-2">Next Up</span>
            <div
              className="font-black text-gray-700 leading-none text-center"
              style={{ fontSize: 'clamp(60px, 16vw, 180px)', lineHeight: 1 }}
            >
              {next.number}{next.letter || ''}
            </div>
            <div
              className="font-black text-gray-700 text-center leading-tight mt-2"
              style={{ fontSize: 'clamp(28px, 6vw, 75px)', letterSpacing: '0.04em' }}
            >
              {`${next.firstName || ''} ${next.lastName || ''}`.trim().toUpperCase()}
            </div>
            {next.clubName && (
              <div
                className="text-gray-400 text-center font-semibold mt-2"
                style={{ fontSize: 'clamp(16px, 3vw, 42px)' }}
              >
                {next.clubName}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-4xl text-gray-300 font-bold">Panel {panel}</span>
          </div>
        )}

        {/* Bottom: next up bar */}
        {performer && next && (
          <div className="border-t border-gray-200 py-3 px-6 flex items-center gap-4 bg-gray-50 shrink-0">
            <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">Next Up</span>
            <span className="text-lg font-bold text-gray-700">{nName}</span>
            <span className="text-sm text-gray-500">#{next.number}{next.letter ? next.letter : ''}</span>
            {next.clubName && <span className="text-sm text-gray-400">{next.clubName}</span>}
          </div>
        )}
      </div>
    );
  }

  // ─── Scores mode (4-panel grid) ───────────────────────────────────────────────
  return (
    <div className="h-screen bg-white flex flex-col relative overflow-hidden">
      <FullscreenBtn />

      {/* 2×2 Grid — full height, no header */}
      <div className="flex-1 grid grid-cols-2 grid-rows-2 min-h-0">
        {PANEL_CELLS.map((cell, idx) => {
          const score = cellScore(latestScores, cell, panel);
          const name  = score?.gymnastName || '';
          const parts = name.split(' ');
          const first = parts[0] || '';
          const last  = parts.slice(1).join(' ') || '';
          const isRight  = idx % 2 === 1;
          const isBottom = idx >= 2;

          return (
            <div
              key={cell.label}
              className={`flex flex-col overflow-hidden ${!isRight ? 'border-r-2' : ''} ${!isBottom ? 'border-b-2' : ''} border-gray-200`}
            >
              {/* Apparatus label — slim */}
              <div className="flex items-center justify-center py-2 border-b border-gray-100 shrink-0">
                <span className="text-black font-black tracking-[0.2em]" style={{ fontSize: 'clamp(14px, 1.5vw, 26px)' }}>{cell.label}</span>
              </div>

              {/* Score content — fills remaining cell height */}
              <div className="flex-1 flex flex-col items-center justify-center py-2 px-3 min-h-0 gap-1">
                {score ? (
                  <>
                    {/* Gymnast name — scales to fill cell */}
                    <div className="flex flex-col items-center w-full leading-tight text-center">
                      <span className="font-black text-gray-900 leading-none" style={{ fontSize: 'clamp(28px, 4.5vw, 90px)' }}>{first}</span>
                      {last && <span className="font-black text-gray-900 leading-none mt-1" style={{ fontSize: 'clamp(28px, 4.5vw, 90px)' }}>{last}</span>}
                    </div>
                    {/* Score box — big */}
                    <div className="flex flex-col items-center gap-1 shrink-0 mt-2">
                      {score.isCorrected && (
                        <span className="font-black tracking-widest text-orange-600 uppercase bg-orange-100 px-3 py-0.5 rounded" style={{ fontSize: 'clamp(10px, 1vw, 14px)' }}>CORRECTION</span>
                      )}
                      <div className="bg-black px-6 py-3 rounded-lg">
                        <span className="font-black text-white tabular-nums" style={{ fontSize: 'clamp(48px, 6.5vw, 120px)', lineHeight: 1 }}>
                          {parseFloat(score.finalScore || 0).toFixed(3)}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <span className="text-gray-200 font-black" style={{ fontSize: 'clamp(40px, 8vw, 120px)' }}>—</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
